import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConversations,
  fetchMessengerCheckInClientsList,
  resetUnreadCountThunk,
  updateConversationUnreadCount,
} from "../../redux/Slices/messengerSlice";
import { fetchTwilioToken } from "../../services/twilioService";
import { initializeTwilioClient } from "../../services/twilioClient";

import Sidebar from "./Sidebar";
import ChatArea from "./ChatArea";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import Loader from "../../components/Common/Loader";

const Messenger = () => {
  const dispatch = useDispatch();
  useFetchCoachProfile();

  const { conversations: rawConversations } = useSelector(
    (state) => state.messenger
  );
  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;
  const coachFriendlyName = profileData?.friendlyName;

  // Memoize the conversations to prevent unnecessary recalculations
  const conversations = useMemo(() => rawConversations, [rawConversations]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [twilioClient, setTwilioClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [activeTab, setActiveTab] = useState("messages");
  // Track current filter option
  const [currentFilter, setCurrentFilter] = useState("all");

  // Track if initial load has been done
  const initialLoadDone = useRef(false);

  // Function to fetch conversations - always fetches on initial load
  const fetchConversationsData = async (forceRefresh = false) => {
    try {
      // Always fetch on initial load or when forceRefresh is true
      if (!initialLoadDone.current || forceRefresh) {
        await dispatch(fetchConversations({ coachId })).unwrap();
        initialLoadDone.current = true;
      }

      // Always fetch check-in clients list
      dispatch(fetchMessengerCheckInClientsList({ coachId }));
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  // Effect for fetching conversations on mount and tab changes
  useEffect(() => {
    if (!coachId) return;

    const initializeMessenger = async () => {
      try {
        await fetchConversationsData(false);
      } catch (error) {
        console.error("Error initializing Messenger:", error);
      }
    };

    initializeMessenger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, coachId, activeTab]);

  // Function to load all paginated conversations from Twilio
  const loadAllTwilioConversations = async (twilioClient) => {
    try {
      if (
        !twilioClient ||
        typeof twilioClient.getSubscribedConversations !== "function"
      ) {
        console.warn("Twilio client not ready or method missing");
        return [];
      }

      let allConversations = [];
      let paginator = await twilioClient.getSubscribedConversations();

      // Load first page
      if (paginator?.items && Array.isArray(paginator.items)) {
        allConversations = [...paginator.items];
      }

      // Load remaining pages if they exist
      while (paginator?.hasNextPage) {
        try {
          paginator = await paginator.nextPage();
          if (paginator?.items && Array.isArray(paginator.items)) {
            allConversations = [...allConversations, ...paginator.items];
          }
        } catch (error) {
          console.error("Error fetching next page of conversations:", error);
          break;
        }
      }

      // console.log(
      //   `Loaded ${allConversations.length} total Twilio conversations`
      // );
      return allConversations;
    } catch (error) {
      console.error("Error loading all Twilio conversations:", error);
      return [];
    }
  };

  const processConversations = async () => {
    try {
      // Load all conversations using pagination
      const allConversations = await loadAllTwilioConversations(twilioClient);

      if (!allConversations || allConversations.length === 0) {
        console.warn("No subscribed conversations found");
        return;
      }

      for (const convo of allConversations) {
        try {
          const participants = await convo.getParticipants();
          const coachParticipant = participants.find(
            (p) => p.identity === coachFriendlyName
          );

          if (!coachParticipant) continue;

          const handleUnreadUpdate = async () => {
            const messagesPaginator = await convo?.getMessages();
            const allMessages = messagesPaginator?.items || [];
            
            const unreadMessages = allMessages.filter(
              (msg) =>
                msg.author !== coachFriendlyName &&
                msg.index > (coachParticipant?.lastReadMessageIndex ?? -1)
            );

            const unreadCount = unreadMessages?.length;

            const lastMessage = allMessages[allMessages.length - 1];
            const lastMessageDate = lastMessage?.dateCreated ?? null;

            dispatch(
              updateConversationUnreadCount({
                conversationId: convo.sid,
                unreadCount: unreadCount || 0,
                lastMessageTime: lastMessageDate
                  ? lastMessageDate.toISOString()
                  : null,
                dateCreated:
                  convo._internalState.dateCreated?.toISOString() ?? null,
              })
            );
          };

          // Prevent duplicate listeners
          if (!convo._hasCustomListeners) {
            // Listen for new messages
            convo.on("messageAdded", (message) => {
              try {
                const parsed = JSON.parse(message.body);
                if (parsed.messageType === "CheckIn") {
                  dispatch(fetchMessengerCheckInClientsList({ coachId }));
                }
              } catch (e) {
                // Not a valid JSON or no messageType, ignore
              }

              handleUnreadUpdate();
            });

            // Listen for changes in participant read status
            convo.on("participantUpdated", ({ participant, updateReasons }) => {
              if (
                participant.identity === coachFriendlyName &&
                updateReasons.includes("lastReadMessageIndex")
              ) {
                handleUnreadUpdate();
              }
            });

            convo._hasCustomListeners = true;
          }

          // Initial unread count update
          await handleUnreadUpdate();
        } catch (error) {
          console.error(`Error processing conversation ${convo.sid}:`, error);
        }
      }
    } catch (error) {
      console.error("Error processing conversations:", error);
    }
  };

  // Effect for initializing Twilio client
  useEffect(() => {
    if (!coachFriendlyName || twilioClient) return;

    const initializeTwilio = async () => {
      try {
        const token = await fetchTwilioToken(coachFriendlyName);
        const client = await initializeTwilioClient(token, coachFriendlyName);
        setTwilioClient(client);

        if (client) {
          await processConversations();
        }

        setLoading(false);
      } catch (error) {
        console.error("Error initializing Twilio client:", error);
        setLoading(false);
      }
    };

    initializeTwilio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachFriendlyName]);

  // Process conversations when twilioClient becomes available
  useEffect(() => {
    if (twilioClient && coachFriendlyName) {
      processConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twilioClient, coachFriendlyName]);

  const handleSelectUser = async (chat) => {
    try {
      if (!twilioClient) {
        console.error("Twilio client is not initialized!");
        return;
      }

      if (!chat.conversationId) {
        console.error("Missing conversationId!");
        return;
      }

      // Use getConversationBySid instead of peekConversationBySid to get a writable conversation
      const convo = await twilioClient.getConversationBySid(
        chat.conversationId
      );

      try {
        // Get the latest messages to find the most recent message index
        const messagesPaginator = await convo.getMessages();
        const latestMessages = messagesPaginator.items;

        // Find the most recent message index (if any messages exist)
        const mostRecentMessageIndex =
          latestMessages.length > 0
            ? Math.max(...latestMessages.map((msg) => msg.index))
            : -1;

        // Update Twilio's read status using advanceLastReadMessageIndex
        if (mostRecentMessageIndex >= 0) {
          await convo.advanceLastReadMessageIndex(mostRecentMessageIndex);

          setTimeout(() => {
            convo.advanceLastReadMessageIndex(mostRecentMessageIndex);
          }, 5000);
          // console.log(
          //   `Updated last read message index to: ${mostRecentMessageIndex}`
          // );
        }

        // Update Redux state immediately
        const lastMsg = latestMessages[latestMessages.length - 1];
        const lastMsgDate = lastMsg?.dateCreated ?? null;

        // Update Redux state immediately
        dispatch(
          updateConversationUnreadCount({
            conversationId: convo.sid,
            unreadCount: 0,
            lastMessageTime: lastMsgDate
              ? lastMsgDate.toISOString()
              : null,
            dateCreated: convo.dateCreated?.toISOString() ?? null,
          })
        );

        // Reset unread count in backend/API
        dispatch(resetUnreadCountThunk({ conversationSid: convo.sid }));

        setSelectedUser(chat);
        setConversation(convo);
        // console.log("test_chat", chat);
        // console.log("test_convo", convo);
      } catch (error) {
        console.error("Error in handleSelectUser:", error);
      }
    } catch (error) {
      console.error("Error getting conversation:", error);
    }
  };

  const handleFilterChange = (filterOption) => {
    const validatedFilter = filterOption.trim() === "" ? "all" : filterOption;
    setCurrentFilter(validatedFilter);

    filterOption === "onlyunseen" ? setIsChecked(true) : setIsChecked(false);
    // dispatch(filterConversations({ coachId, filter: filterOption }));
  };

  // Filter and sort conversations
  const filteredConversations = useMemo(() => {
    if (!conversations) return [];

    let filtered = [...conversations];

    // Apply filter based on currentFilter
    if (currentFilter === "onlyunseen") {
      filtered = filtered.filter((conv) => conv.unreadCount > 0);
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      switch (currentFilter) {
        case "oldChats":
          return new Date(a.lastMessageTime) - new Date(b.lastMessageTime);
        // return new Date(a.dateCreated) - new Date(b.dateCreated);

        case "alphabetical":
          return (a.firstName || "").localeCompare(
            b.firstName || "",
            undefined,
            { sensitivity: "base" }
          );
        // return (a.firstName || "").localeCompare(b.firstName || "");

        case "startDate":
          return new Date(a.dateCreated) - new Date(b.dateCreated);

        case "onlyunseen":
          return (b.unreadCount || 0) - (a.unreadCount || 0);

        default: // "all" or default - unread first, then most recent
          // First sort by unreadCount (descending) - unread conversations first
          if (b.unreadCount !== a.unreadCount) {
            return (b.unreadCount || 0) - (a.unreadCount || 0);
          }
          // Then sort by lastMessageTime (descending) - most recent first
          return (
            new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0)
          );
      }
    });
  }, [conversations, currentFilter]);

  if (loading || !twilioClient) return <Loader />;

  return (
    <div className="flex h-100 bg-gray-100">
      <Sidebar
        onSelectUser={handleSelectUser}
        conversationsList={filteredConversations.map((conversation) => ({
          ...conversation,
          isUnread: conversation.unreadMessageCount > 0,
        }))}
        coachId={coachId}
        onFilterChange={handleFilterChange}
        setIsChecked={setIsChecked}
        isChecked={isChecked}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {selectedUser ? (
        <>
          <ChatArea
            selectedUser={selectedUser}
            conversation={conversation}
            coachId={coachId}
            coachFriendlyName={coachFriendlyName}
            setSelectedUser={setSelectedUser}
            callCheckInAPI={fetchConversationsData}
          />
        </>
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

const EmptyState = () => (
  <div className="flex-1 flex items-center justify-center bg-gray-100 text-gray-500">
    <div className="text-center">
      <div className="text-5xl mb-3">📩</div>
      <p>Select a client to open a conversation</p>
    </div>
  </div>
);

export default Messenger;
